import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

import { Accordion, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { PROPERTY_VALUE, SECTION_LABEL, WORKBENCH_HAIRLINE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface WorkbenchAccordionProps {
  /** Section values that start open. All sections are open by default. */
  defaultOpen?: string[];
  /**
   * `'multiple'` (the default, and every existing caller) keeps inspector
   * sections independent. `'single'` is for AUTHORING STAGES, which are not
   * independent: two open stages is how a staged page grows back into the
   * scrolling wall the staging exists to remove.
   */
  type?: 'single' | 'multiple';
  /** Controlled open stage. `type="single"` only. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** `type="single"` only. Defaults to true, matching Radix. */
  collapsible?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * The inspector's accordion root. Radix `type="multiple"` by default —
 * inspector sections are independent, not one-of-N: a user styling type wants
 * Typography AND Dimensions open at once. `type="single"` opts a caller into
 * the one-open-stage model instead; nothing else changes.
 */
export function WorkbenchAccordion({
  defaultOpen,
  type = 'multiple',
  value,
  onValueChange,
  collapsible = true,
  children,
  className,
}: WorkbenchAccordionProps): JSX.Element {
  if (type === 'single') {
    if (value !== undefined) {
      return (
        <Accordion
          type="single"
          collapsible={collapsible}
          value={value}
          onValueChange={onValueChange}
          className={className}
        >
          {children}
        </Accordion>
      );
    }
    return (
      <Accordion
        type="single"
        collapsible={collapsible}
        defaultValue={defaultOpen?.[0]}
        onValueChange={onValueChange}
        className={className}
      >
        {children}
      </Accordion>
    );
  }

  return (
    <Accordion type="multiple" defaultValue={defaultOpen} className={className}>
      {children}
    </Accordion>
  );
}

export interface CollapsibleSectionProps {
  /** Stable id — also the accordion value. */
  value: string;
  title: string;
  /**
   * The section's RESOLVED STATE, printed on the closed header and hidden the
   * moment it opens (the open body already says it, and one string under two
   * type signatures on one screen is the defect this whole surface was
   * redesigned to remove).
   *
   * Not decoration: a disclosure whose closed header says nothing forces the
   * reader to open every section to find out where the work is, which is the
   * scrolling wall wearing a different hat.
   */
  summary?: React.ReactNode;
  /** Optional leading glyph, e.g. the `⇔` on Size variants. */
  icon?: React.ReactNode;
  /** Optional trailing control, e.g. a `+`. Rendered OUTSIDE the trigger. */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * The `Typography` / `Position` / `Dimensions` accordion wrapper.
 *
 * Content is `AccordionPrimitive.Content` rather than Mora's `AccordionContent`
 * on purpose: that wrapper hardcodes `bg-accent text-accent-foreground`, which
 * would paint a tinted slab behind every inspector section and destroy the
 * "structure from alignment, not from boxes" premise the whole recipe rests on.
 * Everything else — the Radix root, item and trigger, including the rotating
 * `CaretDownIcon` — is the shared primitive, unmodified.
 */
export function CollapsibleSection({
  value,
  title,
  summary,
  icon,
  action,
  children,
  className,
}: CollapsibleSectionProps): JSX.Element {
  return (
    <AccordionItem
      value={value}
      className={cn('group border-b px-2.5 last:border-b-0', WORKBENCH_HAIRLINE, className)}
    >
      {/* Grid, not flex: Radix wraps the trigger in its own `Header` element,
          so a `flex-1` on the trigger stretches inside the header rather than
          across the row — which parks the chevron beside the title instead of
          at the far edge. Making the header a `1fr` grid column fixes it
          without reaching into the shared primitive. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
        <AccordionTrigger
          headerClassName="min-w-0"
          className={cn(
            SECTION_LABEL,
            // A fixed 40px row, not vertical padding. Padding let the row's
            // height follow its own text metrics, so five sibling headers
            // measured 36px and 38px on one screen — a control ladder with two
            // rungs 2px apart, which reads as misalignment rather than as a
            // system. 40 is one of the ladder's real rungs and keeps a stage
            // header distinct from the 32px property rows inside it.
            'h-11 md:h-10 min-w-0 flex-1 gap-3 py-0 hover:no-underline hover:text-muted-foreground',
            'data-[state=open]:text-muted-foreground',
            '[&>svg]:size-3 [&>svg]:text-muted-foreground/60',
          )}
        >
          <span className="flex shrink-0 items-center gap-1.5">
            {icon}
            {title}
          </span>
          {summary ? (
            <span
              className={cn(
                PROPERTY_VALUE,
                'min-w-0 flex-1 truncate text-[13px] normal-case tracking-normal group-data-[state=open]:hidden',
              )}
            >
              {summary}
            </span>
          ) : null}
        </AccordionTrigger>
        {action ? <span className="shrink-0">{action}</span> : null}
      </div>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="space-y-1.5 pb-3">{children}</div>
      </AccordionPrimitive.Content>
    </AccordionItem>
  );
}
